import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react'
import PropTypes from 'prop-types'

const STORAGE_KEY = 'theme_preference'
const MEDIA_QUERY = '(prefers-color-scheme: dark)'

const ThemeContext = createContext()

const getSystemTheme = () => {
  try {
    return window.matchMedia(MEDIA_QUERY).matches ? 'dark' : 'light'
  } catch {
    return 'light'
  }
}

const getSavedTheme = () => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY)
    if (saved === 'light' || saved === 'dark' || saved === 'auto') return saved
  } catch { /* noop */ }
  return 'auto'
}

const applyTheme = (resolved) => {
  document.documentElement.setAttribute('data-theme', resolved)
}

export const ThemeProvider = ({ children }) => {
  const [theme, setThemeState] = useState(getSavedTheme)
  const [systemTheme, setSystemTheme] = useState(getSystemTheme)

  const resolvedTheme = theme === 'auto' ? systemTheme : theme

  useEffect(() => {
    const mql = window.matchMedia(MEDIA_QUERY)
    const handler = (e) => setSystemTheme(e.matches ? 'dark' : 'light')
    if (mql.addEventListener) {
      mql.addEventListener('change', handler)
    } else {
      mql.addListener(handler)
    }
    return () => {
      if (mql.removeEventListener) {
        mql.removeEventListener('change', handler)
      } else {
        mql.removeListener(handler)
      }
    }
  }, [])

  useEffect(() => {
    applyTheme(resolvedTheme)
  }, [resolvedTheme])

  const setTheme = useCallback((mode) => {
    setThemeState(mode)
    try {
      localStorage.setItem(STORAGE_KEY, mode)
    } catch { /* noop */ }
  }, [])

  const cycleTheme = useCallback(() => {
    setTheme(theme === 'auto' ? 'light' : theme === 'light' ? 'dark' : 'auto')
  }, [theme, setTheme])

  const value = useMemo(() => ({
    theme,
    resolvedTheme,
    setTheme,
    cycleTheme
  }), [theme, resolvedTheme, setTheme, cycleTheme])

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  )
}

ThemeProvider.propTypes = {
  children: PropTypes.node.isRequired
}

export const useTheme = () => {
  const context = useContext(ThemeContext)
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider')
  }
  return context
}

export default ThemeContext
